import is from '@sindresorhus/is';
import { Children, createContext, type FunctionComponent, type ReactNode, useContext } from 'react';
import { Link } from 'react-router';

import type { JobArchitectureItemStatistics, JobArchitecturePath } from '~/shared/data-contract/job-architecture';
import type { JobCluster } from '~/shared/data-contract/job-cluster';
import type { JobFamily } from '~/shared/data-contract/job-family';
import type { JobLevel } from '~/shared/data-contract/job-level';
import type { JobRole } from '~/shared/data-contract/job-role';
import { Card } from '~/ui/core/card/Card';
import { CardHeader, CardSubtitle, CardTitleGroup } from '~/ui/core/card/CardHeader';
import { BoardTreeConnector } from '~/ui/domain/board/BoardTreeConnector';

import { useJobClusters } from '../api/use-job-clusters';
import { useJobFamilies } from '../api/use-job-families';
import { useJobLevels } from '../api/use-job-levels';
import { useJobRoles } from '../api/use-job-roles';
import { ProfileStatus, Status } from '../components/Status';
import { jobArchitectureCardDefaultHeight } from '../tabs/card-layout';
import { JobArchitectureCardTitle } from '../tabs/overview/JobArchitectureCardTitle';
import { SkeletonCards } from '../tabs/SkeletonCard';
import { buildJobLevelParams, buildJobRoleParams } from '../url-navigation/use-locate-job-architecture-item';

const overviewHref = (architectureId: string, params: URLSearchParams): string =>
    `/job-architecture-v2/${architectureId}/overview?${params.toString()}`;

const selectionHref = (architectureId: string, selection: Readonly<Record<string, string>>): string =>
    overviewHref(architectureId, new URLSearchParams(selection));

export const roleHref = (
    architectureId: string,
    path: JobArchitecturePath,
    role: Pick<JobRole, 'id' | 'versionInfo'>,
): string =>
    overviewHref(
        architectureId,
        buildJobRoleParams(architectureId, role.id, path, new URLSearchParams(), undefined, role.versionInfo?.version),
    );

export const levelHref = (
    architectureId: string,
    path: JobArchitecturePath,
    level: Pick<JobLevel, 'id' | 'versionInfo'>,
): string =>
    overviewHref(
        architectureId,
        buildJobLevelParams(
            architectureId,
            level.id,
            path,
            new URLSearchParams(),
            undefined,
            level.versionInfo?.version,
        ),
    );

const ReadOnlyBoardCard: FunctionComponent<{
    readonly href: string;
    readonly subtitle: ReactNode;
    readonly title: string;
}> = ({ href, subtitle, title }) => (
    <Link className="block rounded-xl text-inherit no-underline" to={href}>
        <Card style={{ minHeight: jobArchitectureCardDefaultHeight }} surface="recessed">
            <CardHeader>
                <CardTitleGroup>
                    <JobArchitectureCardTitle isGenerating={false} title={title} />
                    <CardSubtitle>{subtitle}</CardSubtitle>
                </CardTitleGroup>
            </CardHeader>
        </Card>
    </Link>
);

const NO_ITEM_STATISTICS: JobArchitectureItemStatistics = {
    approvedLevels: 0,
    approvedRoles: 0,
    clustersCount: 0,
    expected: 0,
    familiesCount: 0,
    levelsCount: 0,
    profiles: 0,
    publishedLevels: 0,
    publishedRoles: 0,
    reviewed: 0,
    reviewerLevels: 0,
    reviewerRoles: 0,
    reviewers: 0,
    rolesCount: 0,
};

// A profile's details count at most its reviews, not its levels, so they show no level progress.
const toItemStatistics = (statistics: object | undefined): JobArchitectureItemStatistics =>
    statistics !== undefined && 'levelsCount' in statistics
        ? (statistics as JobArchitectureItemStatistics)
        : NO_ITEM_STATISTICS;

export const ProfileSubtitle: FunctionComponent<{
    readonly item: Pick<JobLevel | JobRole, 'reviewer' | 'versionInfo'> & { readonly statistics?: object };
}> = ({ item }) => (
    <ProfileStatus
        isApproved={item.versionInfo?.isReviewed ?? false}
        profile={item}
        published={item.versionInfo?.isPublished}
        statistics={toItemStatistics(item.statistics)}
    />
);

// Set for entity cards the agent nested under another one: they drop their own frame and items.
const NestedCardContext = createContext(false);

const BoardColumn: FunctionComponent<{ readonly children: ReactNode; readonly testId: string }> = ({
    children,
    testId,
}) =>
    useContext(NestedCardContext) ? (
        <div className="flex flex-col gap-xs" data-testid={testId}>
            {children}
        </div>
    ) : (
        <div className="flex flex-col gap-xs rounded-xxl bg-surface-subtle p-m" data-testid={testId}>
            {children}
        </div>
    );

const childIndent = { marginLeft: 'var(--spacing-8xl)' };
// A nested card may carry its own children below it, so the line turns in at its header, not the block's middle.
const nestedElbowTop = `calc(${jobArchitectureCardDefaultHeight} / 2)`;

/** The cards the agent nested, hung under their parent like the board's own children. */
const ChosenChildren: FunctionComponent<{ readonly children: ReactNode }> = ({ children }) => {
    const chosen = Children.toArray(children);
    return (
        <NestedCardContext.Provider value={true}>
            <div className="flex flex-col gap-xs" style={childIndent}>
                {chosen.map((child, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: the agent's children are positional.
                    <div key={index} className="relative">
                        <BoardTreeConnector elbowTop={nestedElbowTop} isLast={index === chosen.length - 1} />
                        {child}
                    </div>
                ))}
            </div>
        </NestedCardContext.Provider>
    );
};

// A card lists all its items only at the top level and when the agent nested none, so only then are they loaded.
const useShowsAllItems = (chosen: ReactNode): boolean => !useContext(NestedCardContext) && Children.count(chosen) === 0;

/** The agent's nested cards when it chose some; otherwise all items, or none for a card nested itself. */
const CardItems: FunctionComponent<{ readonly all: ReactNode; readonly chosen: ReactNode }> = ({ all, chosen }) => {
    const isNested = useContext(NestedCardContext);
    if (Children.count(chosen) > 0) return <ChosenChildren>{chosen}</ChosenChildren>;
    return isNested ? null : all;
};

/** Child cards hung under their parent with the board's tree connector. */
const BoardChildren: FunctionComponent<{
    readonly items: readonly {
        readonly href: string;
        readonly id: string;
        readonly subtitle: ReactNode;
        readonly title: string;
    }[];
}> = ({ items }) =>
    items.length === 0 ? null : (
        <div className="flex flex-col gap-xs" style={childIndent}>
            {items.map((item, index) => (
                <div key={item.id} className="relative">
                    <BoardTreeConnector isLast={index === items.length - 1} />
                    <ReadOnlyBoardCard href={item.href} subtitle={item.subtitle} title={item.title} />
                </div>
            ))}
        </div>
    );

type JobRoleIds = {
    readonly architectureId: string;
    readonly children?: ReactNode;
    readonly clusterId: string;
    readonly familyId: string;
    readonly roleId: string;
};

/** A job role as the board shows it, with its levels. */
export const JobRoleChatCard: FunctionComponent<JobRoleIds> = ({
    architectureId,
    children,
    clusterId,
    familyId,
    roleId,
}) => {
    const { data: roles, isPending } = useJobRoles(architectureId, clusterId);
    const role = roles?.find(({ id }) => id === roleId);
    const showsAllItems = useShowsAllItems(children);
    const { data: levels = [] } = useJobLevels(architectureId, roleId, {
        enabled: showsAllItems && (role?.childCount ?? 0) > 0,
    });

    if (isPending) return <SkeletonCards count={1} />;
    if (is.undefined(role)) return null;

    const path: JobArchitecturePath = { cluster: clusterId, family: familyId, role: roleId };
    return (
        <BoardColumn testId="job-role-chat-card">
            <ReadOnlyBoardCard
                href={roleHref(architectureId, path, role)}
                subtitle={<ProfileSubtitle item={role} />}
                title={role.title}
            />
            <CardItems
                all={
                    <BoardChildren
                        items={levels.map((level) => ({
                            href: levelHref(architectureId, path, level),
                            id: level.id,
                            subtitle: <ProfileSubtitle item={level} />,
                            title: level.title,
                        }))}
                    />
                }
                chosen={children}
            />
        </BoardColumn>
    );
};

/** One job level under its role, as the board shows it. */
export const JobLevelChatCard: FunctionComponent<JobRoleIds & { readonly levelId: string }> = ({
    architectureId,
    clusterId,
    familyId,
    levelId,
    roleId,
}) => {
    const { data: roles, isPending: isRolePending } = useJobRoles(architectureId, clusterId);
    const role = roles?.find(({ id }) => id === roleId);
    const { data: levels, isPending } = useJobLevels(architectureId, roleId);
    const level = levels?.find(({ id }) => id === levelId);
    const isNested = useContext(NestedCardContext);

    if (isPending || isRolePending) return <SkeletonCards count={1} />;
    if (is.undefined(role) || is.undefined(level)) return null;

    const path: JobArchitecturePath = { cluster: clusterId, family: familyId, role: roleId };
    if (isNested) {
        return (
            <ReadOnlyBoardCard
                href={levelHref(architectureId, path, level)}
                subtitle={<ProfileSubtitle item={level} />}
                title={level.title}
            />
        );
    }
    return (
        <BoardColumn testId="job-level-chat-card">
            <ReadOnlyBoardCard
                href={roleHref(architectureId, path, role)}
                subtitle={<ProfileSubtitle item={role} />}
                title={role.title}
            />
            <BoardChildren
                items={[
                    {
                        href: levelHref(architectureId, path, level),
                        id: level.id,
                        subtitle: <ProfileSubtitle item={level} />,
                        title: level.title,
                    },
                ]}
            />
        </BoardColumn>
    );
};

const clusterItem = (architectureId: string, familyId: string, cluster: JobCluster) => ({
    href: selectionHref(architectureId, { cluster: cluster.id, family: familyId }),
    id: cluster.id,
    subtitle: <Status statistics={cluster.statistics} />,
    title: cluster.title,
});

/** A job cluster with its roles, as the board shows it. */
export const JobClusterChatCard: FunctionComponent<{
    readonly architectureId: string;
    readonly children?: ReactNode;
    readonly clusterId: string;
    readonly familyId: string;
}> = ({ architectureId, children, clusterId, familyId }) => {
    const { data: clusters, isPending } = useJobClusters(architectureId, familyId);
    const cluster = clusters?.find(({ id }) => id === clusterId);
    const { data: roles = [] } = useJobRoles(architectureId, clusterId, { enabled: useShowsAllItems(children) });

    if (isPending) return <SkeletonCards count={1} />;
    if (is.undefined(cluster)) return null;

    const path: JobArchitecturePath = { cluster: clusterId, family: familyId };
    const { href, subtitle, title } = clusterItem(architectureId, familyId, cluster);
    return (
        <BoardColumn testId="job-cluster-chat-card">
            <ReadOnlyBoardCard href={href} subtitle={subtitle} title={title} />
            <CardItems
                all={
                    <BoardChildren
                        items={roles.map((role) => ({
                            href: roleHref(architectureId, path, role),
                            id: role.id,
                            subtitle: <ProfileSubtitle item={role} />,
                            title: role.title,
                        }))}
                    />
                }
                chosen={children}
            />
        </BoardColumn>
    );
};

const familyHref = (architectureId: string, family: JobFamily): string =>
    selectionHref(architectureId, { family: family.id });

/** A job family with its clusters, as the board shows it. */
export const JobFamilyChatCard: FunctionComponent<{
    readonly architectureId: string;
    readonly children?: ReactNode;
    readonly familyId: string;
}> = ({ architectureId, children, familyId }) => {
    const { data: families, isPending } = useJobFamilies(architectureId);
    const family = families?.find(({ id }) => id === familyId);
    const { data: clusters = [] } = useJobClusters(architectureId, familyId, { enabled: useShowsAllItems(children) });

    if (isPending) return <SkeletonCards count={1} />;
    if (is.undefined(family)) return null;

    return (
        <BoardColumn testId="job-family-chat-card">
            <ReadOnlyBoardCard
                href={familyHref(architectureId, family)}
                subtitle={<Status statistics={family.statistics} />}
                title={family.title}
            />
            <CardItems
                all={
                    <BoardChildren items={clusters.map((cluster) => clusterItem(architectureId, familyId, cluster))} />
                }
                chosen={children}
            />
        </BoardColumn>
    );
};
